import { useState } from 'react';

import { EditableChatInput } from '@/components/EditableChatInput';
import { MessageProps } from '@/components/Message';
import { Tooltip } from '@/design-system';
import MaximizeIcon from '@/icons/MaximizeIcon.svg?react';
import MinimizeIcon from '@/icons/MinimizeIcon.svg?react';
import { MessageUnionType, RunSchema, S3DataSchema } from '@/types/schema';

import { FoldedMessageList } from './FoldedMessageList';

export function RunPageMessages(props: {
  messages: MessageUnionType[];
  objectKey?: string;
  className?: string;
  attachments?: Record<string, S3DataSchema>;
  foldDistractions?:
    | { inputs: RunSchema['inputs'] }
    | { prevRun: RunSchema | undefined };
  onEdit?: (messages: MessageUnionType[]) => void;
  mergeProps?: (
    message: MessageUnionType,
    index: number
  ) => Partial<Omit<MessageProps, 'kwargs'>>;
}) {
  const [collapsedList, setCollapsedList] = useState<boolean[]>(
    props.messages.map(() => false)
  );

  const someOpen = collapsedList.some((v) => !v);

  if (!props.objectKey) {
    return props.onEdit ? (
      <EditableChatInput
        messages={props.messages}
        setMessages={(m: MessageUnionType[]) => {
          props.onEdit?.(m);
        }}
        preserveMessageFormat
        messageFormat="oai"
        debounceUpdate
      />
    ) : (
      <FoldedMessageList
        messages={props.messages}
        attachments={props.attachments}
        foldDistractions={props.foldDistractions}
        mergeProps={props.mergeProps}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-secondary p-4">
      <div className="flex items-center gap-2 text-sm">
        <div className="flex items-center gap-2 font-semibold uppercase text-tertiary">
          <span>Messages</span>
        </div>

        {props.objectKey && (
          <span className="inline rounded border border-secondary px-1 font-mono font-normal text-tertiary">
            {props.objectKey}
          </span>
        )}

        <span className="rounded bg-tertiary px-1 font-medium text-tertiary">
          {props.messages.length}
        </span>
        <Tooltip title={someOpen ? 'Collapse all' : 'Expand all'}>
          <button
            type="button"
            className="stroke-current"
            onClick={() =>
              setCollapsedList((prev) =>
                prev.map(() => (someOpen ? true : false))
              )
            }
          >
            {someOpen ? (
              <MinimizeIcon className="h-5 w-5" />
            ) : (
              <MaximizeIcon className="h-5 w-5" />
            )}
          </button>
        </Tooltip>
      </div>

      {props.onEdit ? (
        <EditableChatInput
          messages={props.messages}
          setMessages={(m: MessageUnionType[]) => {
            props.onEdit?.(m);
          }}
          preserveMessageFormat
          messageFormat="oai"
          debounceUpdate
        />
      ) : (
        <FoldedMessageList
          messages={props.messages}
          attachments={props.attachments}
          foldDistractions={props.foldDistractions}
          mergeProps={props.mergeProps}
          collapsedList={collapsedList}
          onCollapse={(index, collapsed) => {
            setCollapsedList((prev) => {
              const newList = [...prev];
              newList[index] = collapsed;
              return newList;
            });
          }}
        />
      )}
    </div>
  );
}
